import { useEffect, useRef } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { X } from "lucide-react";
import { IconButton } from "@dfws/ui";
import { ChannelList } from "./ChannelList";
import { Conversation } from "./Conversation";
import { closeWindow, setPanelOpen, toggleMinimized, useDock } from "./dock";
import { useOpenChat } from "./useOpenChat";
import s from "./Chat.module.css";

/** The chat side panel and the docked conversation windows. Desktop only; phones use the /chat route. */
export function ChatDock({ me }: { me: string }) {
  const { windows, panelOpen } = useDock();
  const navigate = useNavigate();
  const path = useRouterState({ select: (st) => st.location.pathname });
  const openChat = useOpenChat(false);
  const panel = useRef<HTMLElement>(null);
  const onChatRoute = path.startsWith("/chat");

  useEffect(() => {
    if (!panelOpen) return;
    panel.current?.querySelector<HTMLInputElement>("input[type=search]")?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !document.querySelector("[role=dialog]") && setPanelOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);

  return (
    <>
      {panelOpen && !onChatRoute && (
        <aside ref={panel} className={s.panel} aria-label="Chat">
          <header className={s.panelHeader}>
            <h2 className={s.panelTitle}>Chat</h2>
            <IconButton label="Close chat panel" shortcut="Esc" icon={<X />} size="sm" onClick={() => setPanelOpen(false)} />
          </header>
          <ChannelList
            me={me}
            onOpen={(id) => {
              openChat(id);
              setPanelOpen(false);
            }}
          />
        </aside>
      )}
      {!onChatRoute && windows.length > 0 && (
        <div className={s.dock}>
          {windows.map((w) => (
            <Conversation
              key={w.id}
              channelId={w.id}
              me={me}
              variant="dock"
              minimized={w.minimized}
              onMinimize={() => toggleMinimized(w.id)}
              onClose={() => closeWindow(w.id)}
              onExpand={() => {
                closeWindow(w.id);
                void navigate({ to: "/chat/$channelId", params: { channelId: w.id } });
              }}
            />
          ))}
        </div>
      )}
    </>
  );
}
